import React, {Fragment, memo, useEffect} from "react";
import PropTypes from "prop-types";
import {Link, useNavigate} from "react-router-dom";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Media,
  UncontrolledDropdown,
} from "reactstrap";
import {FaTag, FaUser} from "react-icons/fa";
import {IoLogOutOutline, IoSettingsSharp} from "react-icons/io5";
import {MdSupport} from "react-icons/md";
import {useDispatch, useSelector} from "react-redux";
import Gravatar from "react-gravatar";
import {useMediaQuery} from "react-responsive";
import humanizeString from "string-humanize";
import {logout} from "../../service/logout";
import NotificationMenu from "../Notification/NotificationMenu";
import Breadcrumb from "./components/Breadcrumb";
import SafeView from "../safeView/SafeView";
import ProtectedContent from "../protectedContent";
import {
  requestCustomerUserProfile,
  requestUserProfile,
} from "../../redux/Actions/users.action";
import {requestOrganization} from "../../redux/Actions/root.actions";
import {getUserProfile} from "../../redux/Selectors/users.selector";
import {getCurrentCustomer} from "../../redux/Selectors/customers.selectors";
import "./navbar.scss";

const AppNavbar = (props) => {
  const {brandText} = props;
  const profile = useSelector(getUserProfile);
  const currentCustomer = useSelector(getCurrentCustomer);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isMobile = useMediaQuery({query: "(max-width: 659px)"});

  const handleRouting = (path) => {
    navigate(path);
  };

  useEffect(() => {
    dispatch(requestUserProfile());
    dispatch(requestOrganization());
  }, [dispatch]);

  useEffect(() => {
    if (currentCustomer.id) {
      dispatch(requestCustomerUserProfile());
    }
  }, [dispatch, currentCustomer]);

  const handleLogout = () => {
    navigate("/auth/login");
    logout();
    window.location.reload();
  };

  return (
    <Fragment>
      {!isMobile && (
        <div id="navbar-main">
          <div className="navbar-container">
            <SafeView>
              <div className="navbar-container d-flex align-items-center justify-content-between">
                <div
                  className="navbar--name roboto-bold"
                  style={{fontSize: "20px"}}
                  data-cy={"brand-text"}
                >
                  {humanizeString(brandText).toUpperCase()}
                </div>
                <div className="align-items-center d-flex">
                  <ProtectedContent perms={["view_notification"]}>
                    <NotificationMenu />
                  </ProtectedContent>
                  <UncontrolledDropdown nav>
                    <DropdownToggle className="pr-0" data-cy={"navbar-menu"} nav>
                      <Media className="align-items-center" data-cy="userIcon">
                        <Gravatar
                          email={profile.email || "test@example.com"}
                          rating="pg"
                          default="identicon"
                          className="avatar avatar-sm rounded-circle border-darker"
                        />
                        <Media>
                          <span className="mb-0 ml-1 test_colour roboto-bold navbar--name">
                            {profile.first_name
                              ? profile.first_name
                              : profile.username || "profile"}
                          </span>
                        </Media>
                      </Media>
                    </DropdownToggle>
                    <DropdownMenu className="dropdown-menu-arrow" right>
                      <DropdownItem className="noti-title" header tag="div">
                        <h6 className="text-overflow m-0">Welcome!</h6>
                      </DropdownItem>
                      <DropdownItem
                        onClick={() => {
                          handleRouting("/app/user-profile");
                        }}
                        data-cy={"navbar-profile"}
                        className="cursor-pointer"
                      >
                        <FaUser />
                        <span>My profile</span>
                      </DropdownItem>
                      <DropdownItem to="/app/settings" tag={Link}>
                        <IoSettingsSharp />
                        <span>Settings</span>
                      </DropdownItem>
                      <DropdownItem
                        href="https://support.energyresourceproducts.com"
                        target="_blank"
                      >
                        <MdSupport />
                        <span>Support</span>
                      </DropdownItem>
                      <DropdownItem divider />
                      <DropdownItem>
                        <FaTag />
                        <span>v{import.meta.env.VITE_VERSION}</span>
                      </DropdownItem>
                      <DropdownItem divider />
                      <DropdownItem
                        onClick={handleLogout}
                        className="cursor-pointer"
                        data-cy={"navbar-logout"}
                      >
                        <IoLogOutOutline />
                        <span data-cy="logout">Logout</span>
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </div>
              </div>
            </SafeView>
          </div>
          <SafeView>
            <div className="breadcrumb-container d-flex align-items-center">
              <Breadcrumb />
            </div>
          </SafeView>
        </div>
      )}
    </Fragment>
  );
};

AppNavbar.propTypes = {
  /** This text will be shown as heading for the page */
  brandText: PropTypes.string.isRequired,
};

export default memo(AppNavbar);
